import Link from 'next/link';
import { useMemo } from 'react';
import Card from '../../atoms/Card';
import Tag from '../../atoms/Tag';
import { dateFormatter } from 'ui/helpers/utils';
import eventClickPromo from 'ui/lib/gtm/eventClickPromo';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import useIsNativeView from '../../../hooks/useIsNativeView';
import { useRouter } from 'next/router';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import eventClickDetailBranchSales from '../../../helpers/emarsys/branch-sales/eventClickDetailBranchSales';
import { PAGE_NAME_BRANCH_SALES } from '../../../constants/emarsys/branch-sales';
import { Province } from '../../../interface';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import { FEATURE } from '../../../constants/emarsys/feature';
import eventClickListingPromo from '../../../helpers/emarsys/promo/eventClickListingPromo';
import { PAGE_NAME_PROMO } from '../../../constants/emarsys/promo';

interface ICardPromoProps {
  idPage: string;
  title: string;
  slug: string;
  category: string;
  coverImg: string;
  startDate: string;
  endDate: string;
  tag?: {
    id: string;
    tagName: string;
  };
  altImgText: string;
  classNames?: string;
  index: number;
  handleCollectDataLayer?: (data: {
    id: string;
    name: string;
    creative: string;
    position: string;
    category: string;
  }) => void;
  breadcrumbItems?: {
    name: string;
    path?: string;
    cssClassname?: string;
  }[];
  categoryEmarsys?: string;
  page?: number | null;
  feature?: string;
  branchProvince?: Province;
  pageTitle?: string;
  city?: string;
  cityCode?: string;
  href?: string;
  onClickNative?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isLoadEager?: boolean;
}

const CardPromo = ({
  slug,
  category,
  index,
  href,
  idPage = '',
  title = '',
  coverImg = '',
  startDate = '',
  endDate = '',
  tag = null,
  altImgText = '',
  classNames = '',
  feature = '',
  breadcrumbItems = [],
  branchProvince,
  categoryEmarsys = '',
  page = null,
  pageTitle = '',
  city = '',
  cityCode = '',
  handleCollectDataLayer = () => {},
  onClickNative = () => {},
  isLoadEager = false,
}: ICardPromoProps) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const isNativeView = useIsNativeView();
  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };

  const formattedStartDate = useMemo(() => {
    return dateFormatter(startDate);
  }, [startDate]);

  const formattedEndDate = useMemo(() => {
    return dateFormatter(endDate);
  }, [endDate]);

  const PUBLIC_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;

  const pageCurrent = router?.query?.page ? parseInt(router?.query?.page as string) : 1;

  const handleClick = (e) => {
    // Prevent default and invoke onClickNative if it's from native
    if (isNativeView) {
      e.preventDefault();
      onClickNative(e);
    }

    if (feature === FEATURE.BRANCH_LISTING_PAGE) {
      eventClickDetailBranchSales({
        dispatch,
        customerNo: userData?.customerNo,
        breadcrumbItems: breadcrumbItems,
        pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
        pageTitle: pageTitle,
        sectionName: 'promo bengkel',
        buttonName: 'pilih promo bengkel',
        newParams: {
          province: branchProvince?.name ?? '',
          province_code: branchProvince?.isocode ?? '',
          promo_name: title ?? '',
          city: city,
          city_code: cityCode,
          ...(breadcrumbItems?.[2]?.name
            ? {
                menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
              }
            : {}),
        },
      });
    } else if (feature === FEATURE.LISTING_PROMO) {
      eventClickListingPromo({
        dispatch,
        customerNo: userData?.customerNo,
        breadcrumbItems: breadcrumbItems,
        pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
        pageTitle: pageTitle,
        sectionName: 'list promo',
        buttonName: 'lihat detail',
        newParams: {
          ...(breadcrumbItems?.[2]
            ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
            : {}),
          page_category: categoryEmarsys?.toLowerCase(),
          promo_name: title,
          page_number: page,
        },
      });
    }

    eventClickPromo({
      title: title,
      slug: slug,
      category: category,
      tag: tag?.id,
      id: idPage,
      index: `${pageCurrent == 1 ? index + 2 : index + 1 + (pageCurrent - 1) * 13}`,
      anotherCategory: (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
        ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
        : 'semua',
    });
  };

  const cardRef = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: idPage,
        name: title?.toLowerCase(),
        creative: `/promosi/${category}/${slug}`,
        position: `${
          pageCurrent == 1 ? index + 2 : index + 2 + (pageCurrent - 1) * 12
        } - promosi ${category?.toLowerCase()?.replace(/-/g, ' ')}`,
        category: (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          : 'semua',
      }),
    []
  );

  return (
    <Link
      ref={cardRef}
      id={`card-promo-${title?.toLowerCase()?.replace(/ /g, '-')}`}
      href={href ? href : `${PUBLIC_URL}/${category}/${slug}`}
      onClick={handleClick}
    >
      <Card className={`h-full border border-reliableBlack10 flex flex-col ${classNames}`}>
        {/* Tag */}
        <div className={`${!tag && 'pb-8'}`}>
          {tag && (
            <Tag
              className="rounded-br"
              severity={`${tag.tagName.toLowerCase() === 'trending' ? 'info' : 'warn'}`}
            >
              {tag.tagName.toLowerCase()}
            </Tag>
          )}
        </div>

        <div className="pt-1 px-1.5 pb-6 lg:pt-2 lg:px-3 lg:pb-5 flex flex-col h-full">
          <div>
            <img
              src={coverImg ? generateImageResizer(coverImg, 320, 'webp') : ''}
              alt={altImgText}
              className="aspect-video object-cover object-center mb-4 "
              loading={isLoadEager ? 'eager' : 'lazy'}
              fetchPriority={isLoadEager ? 'high' : 'low'}
              width={268}
              height={180}
            />
          </div>
          <div className="h-full flex flex-col justify-between gap-2 lg:gap-3">
            <p className="text-sm font-medium text-reliableBlack line-clamp-2 lg:text-lg lg:font-semibold">
              {title}
            </p>
            <p className="text-xs font-normal text-reliableBlack70 lg:text-sm">
              <span>{formattedStartDate}</span>
              <span> - </span>
              <span>{formattedEndDate}</span>
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default CardPromo;
